import type { CardMetric } from '@ctd/agent-contract';
import { formatDurationSeconds } from '~containers/analytics/kpis/format-duration';
import { EMPTY_RATE, formatRate } from '~containers/analytics/kpis/format-rate';
import { DEFAULT_TIMEZONE } from '~utils/timezone';

/** A metric value in its unit: `ratio` is a 0..1 rate, `percentage` is already 0..100. */
export function formatMetricValue({
    unit,
    value,
}: Pick<CardMetric, 'unit' | 'value'>): string {
    if (value === null) {
        return EMPTY_RATE;
    }
    switch (unit) {
        case 'ratio':
            return formatRate(value);
        case 'percentage':
            return `${value.toLocaleString('en-US', {
                maximumFractionDigits: 1,
            })}%`;
        case 'duration_seconds':
            return formatDurationSeconds(value);
        case 'currency':
            return `$${value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        case 'count':
            return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
}

export function formatRatioParts({
    num,
    den,
}: NonNullable<CardMetric['ratio']>): string {
    return `${num.toLocaleString('en-US')} of ${den.toLocaleString('en-US')}`;
}

/** A calendar day (YYYY-MM-DD) as "Sep 30, 2026", whatever the viewer's time zone. */
export function formatCalendarDate(date: string): string {
    return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

/** The America/Toronto calendar day of an instant, as "Sep 30, 2026". */
export function formatTorontoDate(instant: string): string {
    return new Date(instant).toLocaleDateString('en-US', {
        timeZone: DEFAULT_TIMEZONE,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
